import { createContext, useContext, useState, ReactNode, useEffect, useCallback, useRef } from 'react';

interface FullScreenDialogContextType {
    /**
     * Closes the dialog programmatically by calling onClose with reason "programmatic"
     */
    close: () => void;

    /**
     * Toggles whether the escape key should be disabled
     * When disabled, onClose events triggered by escapeKeyDown are suppressed
     */
    disableEsc: () => void;

    /**
     * Scrolls the dialog content to the top
     */
    scrollToTop: () => void;

    /**
     * Internal ref to the content element for scroll control
     * @internal
     */
    contentRef: React.RefCallback<HTMLDivElement>;

    /**
     * Internal state for escape key handling
     * @internal
     */
    isEscDisabled: boolean;

    /**
     * Internal handler for onClose events
     * @internal
     */
    handleClose: (event: object, reason: 'backdropClick' | 'escapeKeyDown' | 'programmatic') => void;

    /**
     * Indicates whether the dialog content has been scrolled
     */
    isScrolled: boolean;
}

const FullScreenDialogContext = createContext<FullScreenDialogContextType | null>(null);

interface FullScreenDialogProviderProps {
    children: ReactNode;
    onClose?: (event: object, reason: 'backdropClick' | 'escapeKeyDown' | 'programmatic') => void;
}

export function FullScreenDialogProvider({ children, onClose }: FullScreenDialogProviderProps) {
    const [isEscDisabled, setIsEscDisabled] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null);

    const contentRef = useCallback((node: HTMLDivElement | null) => {
        setContentElement(node);
    }, []);

    const close = () => {
        onClose?.({}, 'programmatic');
    };

    const disableEsc = () => {
        setIsEscDisabled((prev) => !prev);
    };

    const handleClose = (event: object, reason: 'backdropClick' | 'escapeKeyDown' | 'programmatic') => {
        if (isEscDisabled && reason === 'escapeKeyDown') {
            return;
        }

        onClose?.(event, reason);
    };

    const scrollToTop = () => {
        if (!contentElement) {
            return;
        }

        const dialogContainer = contentElement.closest('.MuiDialog-container');

        if (dialogContainer) {
            if (dialogContainer.classList.contains('MuiDialog-scrollBody')) {
                dialogContainer.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                contentElement.scrollTo({ top: 0, behavior: 'smooth' });
            }
        } else {
            contentElement.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const throttleRef = useRef<number | null>(null);

    const checkScrollPosition = useCallback(() => {
        if (!contentElement) {
            return;
        }

        const dialogContainer = contentElement.closest('.MuiDialog-container');
        let scrollTop = 0;

        if (dialogContainer && dialogContainer.classList.contains('MuiDialog-scrollBody')) {
            scrollTop = dialogContainer.scrollTop;
        } else {
            scrollTop = contentElement.scrollTop;
        }

        setIsScrolled(scrollTop > 0);
    }, [contentElement]);

    const throttledCheckScrollPosition = useCallback(() => {
        if (!throttleRef.current) {
            throttleRef.current = requestAnimationFrame(() => {
                checkScrollPosition();
                throttleRef.current = null;
            });
        }
    }, [checkScrollPosition]);

    useEffect(() => {
        if (!contentElement) {
            return;
        }

        const dialogContainer = contentElement.closest('.MuiDialog-container');
        const scrollElement = dialogContainer?.classList.contains('MuiDialog-scrollBody')
            ? dialogContainer
            : contentElement;

        if (scrollElement) {
            const handleScroll = () => {
                throttledCheckScrollPosition();
            };

            scrollElement.addEventListener('scroll', handleScroll, { passive: true });

            // Check initial scroll position (sem throttle para posição inicial)
            checkScrollPosition();

            return () => {
                scrollElement.removeEventListener('scroll', handleScroll);
                if (throttleRef.current) {
                    cancelAnimationFrame(throttleRef.current);
                    throttleRef.current = null;
                }
            };
        }
    }, [checkScrollPosition, throttledCheckScrollPosition, contentElement]);

    const contextValue: FullScreenDialogContextType = {
        close,
        disableEsc,
        scrollToTop,
        contentRef,
        isEscDisabled,
        handleClose,
        isScrolled,
    };

    return <FullScreenDialogContext.Provider value={contextValue}>{children}</FullScreenDialogContext.Provider>;
}

export function useFullScreenDialog(): FullScreenDialogContextType {
    const context = useContext(FullScreenDialogContext);

    if (!context) {
        throw new Error('useFullScreenDialog must be used within a FullScreenDialogProvider');
    }

    return context;
}
